import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronsDown,
  faChevronsLeft,
  faChevronsRight,
  faChevronsUp,
  faChevronUp
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useAtom } from 'jotai';
import type React from 'react';
import { useMemo, useState } from 'react';

import { isLayoutMobileAtom } from '@/store/layout';

import useStyles from './DualListSelect.styles';
import type { DualListSelectProps } from './DualListSelect.types';

export const DualListSelect: React.FC<DualListSelectProps> = ({
  labelAvailable = 'Available Options',
  labelSelected = 'Selected Options',
  options,
  selected,
  onChange
}) => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [filter, setFilter] = useState('');
  const [leftSelected, setLeftSelected] = useState<string[]>([]);
  const [rightSelected, setRightSelected] = useState<string[]>([]);
  const { classes } = useStyles({ isLayoutMobile });

  const availableOptions = useMemo(
    () => options.filter((opt) => !selected.includes(opt.value)),
    [options, selected]
  );

  const filteredOptions = useMemo(
    () =>
      availableOptions.filter((opt) =>
        opt.label.toLowerCase().includes(filter.toLowerCase())
      ),
    [availableOptions, filter]
  );

  const selectedOptions = useMemo(
    () =>
      selected
        .map((val) => options.find((opt) => opt.value === val))
        .filter((opt): opt is (typeof options)[number] => !!opt),
    [options, selected]
  );

  const handleAdd = () => {
    onChange([...selected, ...leftSelected]);
    setLeftSelected([]);
  };

  const handleAddAll = () => {
    onChange([...selected, ...filteredOptions.map((opt) => opt.value)]);
    setLeftSelected([]);
  };

  const handleRemove = () => {
    onChange(selected.filter((val) => !rightSelected.includes(val)));
    setRightSelected([]);
  };

  const handleRemoveAll = () => {
    onChange([]);
    setRightSelected([]);
  };

  return (
    <Stack
      spacing={1}
      direction={isLayoutMobile ? 'column' : 'row'}
      sx={{
        height: '100%',
        p: 2,
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Available Options */}
      <Box sx={{ height: '100%' }}>
        <div className={classes.dualListSelectPaper}>
          <Typography variant="subtitle1" gutterBottom>
            {labelAvailable}
          </Typography>
          <TextField
            fullWidth
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search..."
            size="small"
            sx={{ mb: 1 }}
            value={filter}
          />
          <List
            className={classes.dualListSelectList}
            dense
            sx={{
              alignItems: filteredOptions.length === 0 ? 'center' : undefined,
              display: filteredOptions.length === 0 ? 'flex' : 'block',
              justifyContent:
                filteredOptions.length === 0 ? 'center' : undefined
            }}
          >
            {filteredOptions.length === 0 ? (
              <Typography
                className={classes.dualListSelectPaperTypography}
                color="textSecondary"
                variant="body2"
              >
                No options available
              </Typography>
            ) : null}
            {filteredOptions.map((opt) => (
              <ListItemButton
                key={opt.value}
                selected={leftSelected.includes(opt.value)}
                onClick={() =>
                  setLeftSelected((prev) =>
                    prev.includes(opt.value)
                      ? prev.filter((v) => v !== opt.value)
                      : [...prev, opt.value]
                  )
                }
              >
                <ListItemText primary={opt.label} />
              </ListItemButton>
            ))}
          </List>
        </div>
      </Box>
      {/* Action Buttons */}
      <Box sx={{ height: '100%' }}>
        <Stack
          spacing={1}
          sx={{
            height: '100%',
            p: 2,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Button
            className={classes.dualListSelectButton}
            disabled={filteredOptions.length === 0}
            onClick={handleAddAll}
            size="small"
            variant="outlined"
            endIcon={
              isLayoutMobile ? (
                <FontAwesomeIcon
                  className={classes.dualListSelectIcon}
                  icon={faChevronsDown}
                />
              ) : (
                <FontAwesomeIcon
                  className={classes.dualListSelectIcon}
                  icon={faChevronsRight}
                />
              )
            }
          >
            Add All
          </Button>
          <Button
            className={classes.dualListSelectButton}
            disabled={leftSelected.length === 0}
            onClick={handleAdd}
            size="small"
            variant="outlined"
            endIcon={
              isLayoutMobile ? (
                <FontAwesomeIcon
                  className={classes.dualListSelectIcon}
                  icon={faChevronDown}
                />
              ) : (
                <FontAwesomeIcon
                  className={classes.dualListSelectIcon}
                  icon={faChevronRight}
                />
              )
            }
          >
            Add
          </Button>
          <Button
            className={classes.dualListSelectButton}
            disabled={rightSelected.length === 0}
            onClick={handleRemove}
            size="small"
            variant="outlined"
            startIcon={
              isLayoutMobile ? (
                <FontAwesomeIcon
                  className={classes.dualListSelectIcon}
                  icon={faChevronUp}
                />
              ) : (
                <FontAwesomeIcon
                  className={classes.dualListSelectIcon}
                  icon={faChevronLeft}
                />
              )
            }
          >
            Remove
          </Button>
          <Button
            className={classes.dualListSelectButton}
            disabled={selected.length === 0}
            onClick={handleRemoveAll}
            size="small"
            variant="outlined"
            startIcon={
              isLayoutMobile ? (
                <FontAwesomeIcon
                  className={classes.dualListSelectIcon}
                  icon={faChevronsUp}
                />
              ) : (
                <FontAwesomeIcon
                  className={classes.dualListSelectIcon}
                  icon={faChevronsLeft}
                />
              )
            }
          >
            Remove All
          </Button>
        </Stack>
      </Box>
      {/* Selected Options */}
      <Box sx={{ height: '100%' }}>
        <div className={classes.dualListSelectPaper}>
          <Typography variant="subtitle1" gutterBottom>
            {labelSelected}
          </Typography>
          <List
            className={classes.dualListSelectList}
            dense
            sx={{
              alignItems: selectedOptions.length === 0 ? 'center' : undefined,
              display: selectedOptions.length === 0 ? 'flex' : 'block',
              justifyContent:
                selectedOptions.length === 0 ? 'center' : undefined
            }}
          >
            {selectedOptions.length === 0 ? (
              <Typography
                className={classes.dualListSelectPaperTypography}
                color="textSecondary"
                variant="body2"
              >
                No options selected
              </Typography>
            ) : null}
            {selectedOptions.map((opt) => (
              <ListItemButton
                key={opt.value}
                selected={rightSelected.includes(opt.value)}
                onClick={() =>
                  setRightSelected((prev) =>
                    prev.includes(opt.value)
                      ? prev.filter((v) => v !== opt.value)
                      : [...prev, opt.value]
                  )
                }
              >
                <ListItemText primary={opt.label} />
              </ListItemButton>
            ))}
          </List>
        </div>
      </Box>
    </Stack>
  );
};
export default DualListSelect;
